import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import { getFirebaseAuth } from "services/firebase/auth";

import {
  isSignedIn,
  isSigningIn,
  isSigningOut
} from "seneca-common/features/user/state";
import { useTypedSearchParams } from "seneca-common/utils/routing/react-router-typesafe-routes/dom";

import { isIE } from "features/common/utils/functions/browser";
import {
  linkToPasswordReset,
  linkToSignIn,
  linkToSignUp
} from "features/sign-up/links";
import signInWithProviderWithRedirect from "features/user/actions/auth/sso/signInWithProviderWithRedirect";
import { ROUTES } from "routing/routes";

import { clearForcedSignOut, setForcedSignOut } from "./forcedSignOut";

export default function useRedirectOnAuthStateChanged() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const userIdRef = useRef<string | null>(null);
  const [{ customAccessToken: token }] = useTypedSearchParams(ROUTES);

  const [initialChangeDone, setInitialChangeDone] = useState<boolean>(false);
  const signingOut = useSelector(isSigningOut);
  const signingIn = useSelector(isSigningIn);
  const signedIn = useSelector(isSignedIn);

  useEffect(() => {
    const unsubscribeFirebaseEventListener = onAuthStateChanged(
      getFirebaseAuth(),
      (user: any) => {
        if (token) return;

        if (!initialChangeDone) {
          setInitialChangeDone(true);
          userIdRef.current = user ? user.uid : null;
          return;
        }
        if (
          !user &&
          !isOnAnonymousPage() &&
          !isIE() &&
          !signingIn &&
          !signingOut &&
          signedIn
        ) {
          setForcedSignOut();
          window.location = (window.location.origin + linkToSignIn()) as any;
        } else if (user && user.uid !== userIdRef.current && !signingIn) {
          window.location.reload();
        } else if (user) {
          clearForcedSignOut();
        }

        userIdRef.current = user ? user.uid : null;
      }
    );

    return () => unsubscribeFirebaseEventListener();
  }, [initialChangeDone, signedIn, signingIn, signingOut, token]);

  useEffect(() => {
    if (token) return;

    dispatch(signInWithProviderWithRedirect(location, navigate));
  }, [location, navigate, dispatch, token]);
}

function isOnAnonymousPage() {
  return [linkToSignIn(), linkToSignUp(), linkToPasswordReset()].some(
    link => window.location.href === window.location.origin + link
  );
}
